import {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import api from "../api";
import {upsertNotification} from "../app-slice";
import {upsertWorkspaceInboxItem} from "../workspace-graph-slice";
import {notificationApi} from "../../models/notification/notification-api";
import {listenForPushNavigation, registerPushWorker, syncPushSubscription} from "./push-client";

// Mounted once for signed-in users: registers the push service worker, keeps this browser's subscription current
// and marks a notification read when the app is opened from it (`?notification=<documentId>` in any route).
export default function PushBridge() {
    const {user} = useSelector((state) => state.auth);
    const location = useLocation();
    const dispatch = useDispatch();
    const userKey = user?.id ?? null;

    useEffect(() => {
        if (!userKey) return undefined;
        const sync = async () => {
            await registerPushWorker();
            if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
            const key = await notificationApi.pushKey().catch(() => null);
            await syncPushSubscription(key?.publicKey);
        };
        sync();
        // the browser rotated the subscription: subscribe again (permission is already granted, no prompt)
        return listenForPushNavigation(sync);
    }, [userKey]);

    useEffect(() => {
        if (!userKey) return;
        const notification = new URLSearchParams(location.search).get("notification");
        if (!notification) return;
        // silent: an old or deleted notification must not show an error toast
        api.put(`/user-event-logs/${encodeURIComponent(notification)}`, {data: {isRead: true}}, {suppressGlobalErrorHandler: true})
            .then((response) => {
                const item = response?.data?.data;
                if (!item) return;
                dispatch(upsertNotification(item));
                dispatch(upsertWorkspaceInboxItem(item));
            })
            .catch(() => {});
    }, [dispatch, location.search, userKey]);

    return null;
}
